import React, { useCallback, useEffect, useState } from 'react';
import { getPerson } from './collect';
import { getCollectResults } from './api';
import CollectResultView from '../tm/components/CollectResultView';
import * as todosApi from '../tm/api-todos';
import type { Collect, PersonCard, Project, ResultRow } from './types';

interface Props {
  refId: string;
  card: PersonCard;
  p: Project;
  edit: (fn: (c: Collect) => void) => void;
  toast: (m: string) => void;
}

/** 항목 → 일일 업무 제목 (2026-10-19 지도사 「일일업무 요청 버튼 → 오재인 지정 · 제목은 엔젤투자교육 이수 요청 뭐 이런 걸로」) */
export function todoTitleOf(label: string): string {
  const t = String(label || '').replace(/\s+/g, ' ').trim();
  if (/엔젤\s*투자\s*교육/.test(t)) return '엔젤투자교육 이수 요청';
  if (/신분증/.test(t)) return '신분증 확인 요청';
  if (/소재지|주소/.test(t)) return t.replace(/\s*(확인|요청)$/, '') + ' 확인 요청';
  return t.replace(/\s*(ID\s*\/\s*PW|아이디.*)$/i, '').trim() + ' 처리 요청';
}
const STAFF_NAME = '오재인';

export default function CollectResult({ refId, card, p, edit, toast }: Props) {
  // 일일 업무 요청 — 오재인에게 · 항목마다 한 번(이 화면을 연 동안) · 민감 값(아이디 · 비밀번호)은 업무 글에 넣지 않는다
  const [asked, setAsked] = useState<Record<string, boolean>>({});
  const [askBusy, setAskBusy] = useState('');
  async function askTodo(item: { id: string; label: string }) {
    if (askBusy) return;
    setAskBusy(item.id);
    try {
      const st = await todosApi.getStaff();
      const to = (st.data || []).find((x) => x.name === STAFF_NAME);
      if (!to) { toast(`${STAFF_NAME} 계정을 찾지 못했어요`); return; }
      const who = card.name || '조합원';
      const r = await todosApi.createTodo({
        title: `[${p.org}] ${todoTitleOf(item.label)} — ${who}`.slice(0, 200),
        description: `벤처인증 › ${p.org} › 1단계 기초정보 수집 › ${who} 자료 「${item.label}」\n제출한 값(아이디 · 비밀번호 등)은 벤처인증 화면에서 확인해 주세요.`,
        assignees: [to.id], due_date: new Date(Date.now() + 9 * 3600 * 1000).toISOString().slice(0, 10),
      });
      if (!r.success) { toast(r.error || '일일 업무를 만들지 못했어요'); return; }
      setAsked((a) => ({ ...a, [item.id]: true }));
      toast(`${STAFF_NAME} 매니저 일일 업무에 넣었어요 — ${todoTitleOf(item.label)}`);
    } catch (e) { toast((e as Error).message || '일일 업무를 만들지 못했어요'); } finally { setAskBusy(''); }
  }
  const [loading, setLoading] = useState(false);
  const cpid = card.collectProjectId || '';

  const sensOf = (title: string): boolean => {
    const it = card.items.find((x) => x.title === title);
    return it ? !!it.sensitive : false;
  };

  // 상태 뱃지(카드/행 status)·문서 자동연동용으로 card.results 동기화 유지
  const load = useCallback(async () => {
    if (!cpid) return;
    setLoading(true);
    try {
      const res = await getCollectResults(p.id, cpid);
      const rows: ResultRow[] = res.rows.map((r) => ({
        title: r.title, type: r.type, required: r.required, sensitive: sensOf(r.title),
        grp: r.section, status: r.status, value: r.value,
        date: '', uploadId: r.uploadId || (r.files[0] && r.files[0].id) || undefined,
      }));
      edit((c) => { getPerson(c, refId).results = rows; });
    } catch (e) { toast('결과 동기화 실패: ' + ((e as Error).message || '')); } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cpid, p.id, refId]);

  useEffect(() => { load(); }, [load]);

  if (!card.sent && !cpid) {
    return <div className="rempty">아직 전송 전입니다 · &apos;리스트 보기&apos;에서 항목을 편집하고 전송하세요</div>;
  }
  if (!cpid) {
    return <div className="rempty">{loading ? '결과를 불러오는 중입니다…' : '아직 제출된 자료가 없습니다 · 고객이 포털에서 제출하면 여기에 표시됩니다'}</div>;
  }

  // TM 자료수집 결과뷰 재사용: 승인/반려/다운로드/💾서버저장/미리보기/PDF편집/일괄 동일.
  // TM 뷰 내부 액션(승인 등) 후 onToast가 호출되므로 그때 card.results 재동기화 → 상태 뱃지 갱신.
  return <CollectResultView projectId={cpid} onToast={(m: string) => { toast(m); load(); }}
    itemAction={(it) => (
      <button type="button" className={'todo-ask' + (asked[it.id] ? ' on' : '')} disabled={!!askBusy || !!asked[it.id]} onClick={(e) => { e.stopPropagation(); void askTodo(it); }}
        title={asked[it.id] ? '일일 업무에 넣었어요' : `${STAFF_NAME} 매니저 일일 업무로 「${todoTitleOf(it.label)}」`}>
        {asked[it.id] ? '요청함' : askBusy === it.id ? '보내는 중…' : '일일업무 요청'}
      </button>
    )} />;
}
